import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface AnalyticsOverview {
  kpis: {
    totalUsers: number
    active7d: number
    active30d: number
    paying: number
    mrr: number
    avgStreak: number
    maxStreak: number
    exercisesLast30d: number
    exerciseMinutesLast30d: number
  }
  dau: { date: string; dau: number }[]
  categoryMinutes: { ear: number; sight: number; knowledge: number; uncategorized: number }
  funnel: {
    registered: number
    practicedOnce: number
    active7d: number
    streakGt3: number
    paying: number
  }
}

export function AnalyticsOverviewPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-analytics-overview'],
    queryFn: () => api.get<AnalyticsOverview>('/api/admin/analytics/overview'),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
    )
  }

  const k = data?.kpis
  const funnel = data?.funnel
  const cat = data?.categoryMinutes
  const dau = data?.dau ?? []

  return (
    <div>
      <h1 className="text-2xl font-heading font-semibold mb-6">Analytics Overview</h1>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Total Users" value={k?.totalUsers?.toLocaleString() ?? '0'} />
        <KpiCard label="Active (7d)" value={k?.active7d?.toLocaleString() ?? '0'} />
        <KpiCard label="Active (30d)" value={k?.active30d?.toLocaleString() ?? '0'} />
        <KpiCard label="Paying" value={k?.paying?.toLocaleString() ?? '0'} />
        <KpiCard label="MRR" value={k?.mrr ? `$${(k.mrr / 100).toFixed(0)}` : '$0'} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Avg Streak" value={`${(k?.avgStreak ?? 0).toFixed(1)} days`} />
        <KpiCard label="Max Streak" value={`${k?.maxStreak ?? 0} days`} />
        <KpiCard label="Exercises (30d)" value={k?.exercisesLast30d?.toLocaleString() ?? '0'} />
        <KpiCard label="Exercise Min (30d)" value={k?.exerciseMinutesLast30d?.toLocaleString() ?? '0'} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* Funnel */}
        {funnel && (
          <Card>
            <CardHeader>
              <h3 className="font-heading font-semibold text-sm">User Funnel</h3>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: 'Registered', value: funnel.registered },
                { label: 'Practiced Once', value: funnel.practicedOnce },
                { label: 'Active (7d)', value: funnel.active7d },
                { label: 'Streak > 3', value: funnel.streakGt3 },
                { label: 'Paying', value: funnel.paying },
              ].map((step, _i, arr) => {
                const maxVal = arr[0]!.value
                const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0
                return (
                  <div key={step.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[var(--content-secondary)]">{step.label}</span>
                      <span className="tabular-nums font-medium">{step.value.toLocaleString()}</span>
                    </div>
                    <div className="h-3 bg-[var(--surface-nested)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#65A0A8] rounded-full transition-all"
                        style={{ width: `${Math.max(2, pct)}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        )}

        {/* Category breakdown */}
        {cat && (
          <Card>
            <CardHeader>
              <h3 className="font-heading font-semibold text-sm">Practice Time by Category (30d)</h3>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: 'Ear Training', value: cat.ear, color: 'bg-emerald-500' },
                { label: 'Sight Reading', value: cat.sight, color: 'bg-blue-500' },
                { label: 'Knowledge', value: cat.knowledge, color: 'bg-amber-500' },
                { label: 'Uncategorized', value: cat.uncategorized, color: 'bg-gray-400' },
              ].map((c) => {
                const total = cat.ear + cat.sight + cat.knowledge + cat.uncategorized
                const pct = total > 0 ? (c.value / total) * 100 : 0
                return (
                  <div key={c.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[var(--content-secondary)]">{c.label}</span>
                      <span className="tabular-nums">{c.value}m ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="h-3 bg-[var(--surface-nested)] rounded-full overflow-hidden">
                      <div className={`h-full ${c.color} rounded-full transition-all`} style={{ width: `${Math.max(1, pct)}%` }} />
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* DAU chart */}
      {dau.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-heading font-semibold text-sm">Daily Active Users (30d)</h3>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[2px] h-32">
              {dau.map((d) => {
                const max = Math.max(...dau.map((x) => x.dau))
                const pct = max > 0 ? (d.dau / max) * 100 : 0
                return (
                  <div
                    key={d.date}
                    className="flex-1 bg-[#65A0A8]/60 hover:bg-[#65A0A8] rounded-t transition-colors"
                    style={{ height: `${Math.max(2, pct)}%` }}
                    title={`${d.date}: ${d.dau} users`}
                  />
                )
              })}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-[var(--content-tertiary)]">
              <span>{dau[0]?.date}</span>
              <span>{dau[dau.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}
